import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { SendHorizonal } from 'lucide-react';

const ConductInterview = () => {
  const { sessionId } = useParams();
  const [sessionData, setSessionData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showCurrentQuestion, setShowCurrentQuestion] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Ref for auto-scrolling
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Enhanced auto-scroll to bottom function
  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };

  // Enhanced load interview data with better error handling
  useEffect(() => {
    if (sessionId) {
      //console.log('Loading interview for session:', sessionId);

      fetch(`https://n8n.acespireconsulting.com/webhook/api/interview?sessionId=${sessionId}`)
        .then(res => {
          //console.log('Response status:', res.status);
          //console.log('Response headers:', res.headers);

          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const contentType = res.headers.get('content-type');
         // console.log('Content type:', contentType);

          return res.clone().text().then(text => {
           // console.log('Raw response text:', text);

            if (!text || text.trim() === '') {
              throw new Error('Empty response from server');
            }

            try {
              return JSON.parse(text);
            } catch (parseError) {
              console.error('JSON parse error:', parseError);
              //console.log('Response that failed to parse:', text);
              throw new Error(`Invalid JSON response: ${parseError.message}`);
            }
          });
        })
        .then(data => {
          //console.log('Parsed data:', data);

          let interviewData;
          if (Array.isArray(data) && data.length > 0) {
            interviewData = data[0];
          } else {
            interviewData = data;
          }

          if (interviewData && interviewData.error) {
            setError(interviewData.error);
          } else if (interviewData && interviewData.candidate_name && interviewData.questions) {
            setSessionData(interviewData);

            if (interviewData.session_status === 'completed') {
              setSubmitted(true);
              setConversationHistory([
                {
                  type: 'ai',
                  message: `Hello ${interviewData.candidate_name}`,
                  submessages: [
                    'Your interview has been completed successfully.',
                    'Thank you for your time. We will review your responses and get back to you soon.'
                  ],
                  timestamp: new Date()
                }
              ]);
            } else {
              setConversationHistory([
                {
                  type: 'ai',
                  message: `Congrats ${interviewData.candidate_name}`,
                  submessages: [
                    'You have been selected for the next round.',
                    'Kindly Answer few questions to assess you better.'
                  ],
                  timestamp: new Date()
                }
              ]);
              setTimeout(() => {
                setShowCurrentQuestion(true);
              }, 1000);
            }
          } else {
            //console.log('Invalid data structure:', interviewData);
            setError('Invalid interview data format - missing candidate_name or questions');
          }
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading interview:', err);
          setError(`Failed to load interview: ${err.message}`);
          setLoading(false);
        });
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && !submitted && sessionData) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !submitted) {
      handleSubmitInterview();
    }
  }, [timeLeft, submitted, sessionData]);

  // Auto-scroll when conversation updates - with delay to ensure DOM updates
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100); // Small delay to ensure DOM is updated
    
    return () => clearTimeout(timer);
  }, [conversationHistory, showCurrentQuestion, currentAnswer]);

  const handleAnswerChange = (value) => {
    setCurrentAnswer(value);
  };

  const handleNextQuestion = () => {
    if (!currentAnswer.trim()) return;

    const question = sessionData.questions[currentQuestion];

    const newHistory = [
      ...conversationHistory,
      {
        type: 'ai',
        message: question.question,
        category: question.category,
        questionNumber: currentQuestion + 1,
        timestamp: new Date()
      },
      {
        type: 'user',
        message: currentAnswer,
        timestamp: new Date()
      }
    ];

    setConversationHistory(newHistory);

    const newAnswers = {
      ...answers,
      [currentQuestion]: currentAnswer
    };
    setAnswers(newAnswers);
    setCurrentAnswer('');

    if (currentQuestion < sessionData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowCurrentQuestion(true);
      // Scroll after state updates
      setTimeout(() => scrollToBottom(), 200);
    } else {
      setShowCurrentQuestion(false);
      setSubmitting(true);
      setTimeout(() => {
        const closingHistory = [
          ...newHistory,
          {
            type: 'ai',
            message: `Thank you for your time, ${sessionData.candidate_name}! We'll review your responses and get back to you soon with next steps.`,
            timestamp: new Date()
          }
        ];
        setConversationHistory(closingHistory);
        handleSubmitInterview(newAnswers);
      }, 1000);
    }
  };

  const handleSubmitInterview = (finalAnswers = null) => {
    const answersToSubmit = finalAnswers || {
      ...answers,
      [currentQuestion]: currentAnswer
    };

    //console.log('Submitting interview with answers:', answersToSubmit);
    //console.log('Session ID:', sessionId);

    fetch(`https://n8n.acespireconsulting.com/webhook/api/interview/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sessionId: sessionId,
        answers: answersToSubmit,
        completedAt: new Date().toISOString()
      })
    })
      .then(res => {
        //console.log('Submit response status:', res.status);

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return res.json();
        } else {
          return res.text().then(text => ({ success: true, message: text }));
        }
      })
      .then(data => {
        //console.log('Submit response data:', data);
        setSubmitting(false);
        setSubmitted(true);
      })
      .catch(err => {
        console.error('Error submitting interview:', err);
        setSubmitting(false);

        setConversationHistory(prev => [
          ...prev,
          {
            type: 'ai',
            message: 'There was an issue submitting your interview. Your responses have been recorded. Please contact support if needed.',
            timestamp: new Date(),
            isError: true
          }
        ]);

        setSubmitted(true);
      });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNextQuestion();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-3xl shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-center">Loading interview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md">
          <div className="text-red-600 text-center">
            <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <h2 className="text-xl font-semibold mb-2">Interview Not Found</h2>
            <p className="text-gray-600">{error}</p>
            {submitting && (
              <div className="flex justify-center mt-4">
                <div className="bg-gray-100 rounded-2xl p-4 flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span className="text-gray-600">Submitting your responses...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!sessionData || !sessionData.questions) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md">
          <div className="text-gray-600 text-center">
            <h2 className="text-xl font-semibold mb-2">No Interview Data</h2>
            <p>Interview session not found or expired.</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted || (sessionData && sessionData.session_status === 'completed')) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md text-center">
          <div className="text-green-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Interview Completed!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your time, {sessionData.candidate_name}. We'll review your responses and get back to you soon.
          </p>
          {sessionData && sessionData.completed_at && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                Completed on: {new Date(sessionData.completed_at).toLocaleDateString()} at {new Date(sessionData.completed_at).toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / sessionData.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      {/* Main Chat Container - Fixed height with proper scroll */}
      <div
        className="w-full h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 10%, rgba(250, 247, 249, 0.9) 20%, rgba(255, 149, 229, 0.4) 50%, rgba(121, 188, 255, 0.3) 80%, rgba(255, 255, 255, 0.8) 95%)'
        }}
      >
        {/* Header with Logo and Timer */}
        <div className="p-3 sm:p-4 text-center flex-shrink-0 border-b border-white/20">
          <div className="flex items-center justify-between">
            {/* Empty left space for balance */}
            <div className="w-16 sm:w-20"></div>

            {/* Centered Logo and Text */}
            <div className="flex flex-col items-center justify-center space-y-1 sm:space-y-2">
              {/* Custom Logo - Replace with your logo */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                {/* Replace this with: <img src="/path/to/your/logo.png" alt="Hireon Logo" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" /> */}
                 <img src="/assets/Hireon.png" alt="Hireon Logo" className="w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <h1 className="text-base sm:text-lg font-bold text-gray-800">Hireon AI by Acespire</h1>
            </div>

            {/* Timer - Top Right */}
            <div className="text-right w-20 sm:w-20">
              <div className="text-lg sm:text-xl font-bold text-black">
                {formatTime(timeLeft)}
              </div>
              <p className="text-xs text-gray-600 sm:block">Time remaining</p>
            </div>
          </div>
        </div>

        {/* Chat Messages Area - Scrollable with proper padding */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto px-3 sm:px-6 py-2 sm:py-4 space-y-3 sm:space-y-4"
          style={{ 
            minHeight: 0,
            maxHeight: '100%',
            scrollBehavior: 'smooth'
          }}
        >
          {/* Render Conversation History */}
          {conversationHistory.map((msg, index) => (
            <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-xs sm:max-w-md lg:max-w-2xl">
                {msg.type === 'ai' && (
                  <div className="text-xs text-gray-700 mb-2 flex items-center space-x-2">
                    <span className="font-medium">HIREON AI</span>
                    {msg.category && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${msg.category === 'hr'
                        ? 'bg-green-200/80 text-green-800'
                        : 'bg-blue-200/80 text-blue-800'
                        }`}>
                        {msg.category === 'hr' ? 'HR' : 'Technical'}
                      </span>
                    )}
                    {msg.questionNumber && (
                      <span className="text-xs text-gray-600">Q{msg.questionNumber}</span>
                    )}
                  </div>
                )}

                <div className={`rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-sm ${msg.type === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/90 backdrop-blur-sm text-gray-800'
                  }`}>
                  <p className="whitespace-pre-wrap text-sm sm:text-base">{msg.message}</p>
                  {msg.submessages && msg.submessages.map((submsg, idx) => (
                    <p key={idx} className="mt-2 whitespace-pre-wrap text-sm sm:text-base">{submsg}</p>
                  ))}
                </div>

                {msg.type === 'user' && (
                  <div className="text-xs text-gray-600 mt-1 text-right">You</div>
                )}
              </div>
            </div>
          ))}

          {/* Current Question */}
          {showCurrentQuestion && sessionData && sessionData.questions && sessionData.questions[currentQuestion] && (
            <div className="flex justify-start">
              <div className="max-w-xs sm:max-w-md lg:max-w-2xl">
                <div className="text-xs text-gray-700 mb-2 flex items-center space-x-2">
                  <span className="font-medium">HIREON AI</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${sessionData.questions[currentQuestion].category === 'hr'
                    ? 'bg-green-200/80 text-green-800'
                    : 'bg-blue-200/80 text-blue-800'
                    }`}>
                    {sessionData.questions[currentQuestion].category === 'hr' ? 'HR' : 'Technical'}
                  </span>
                  <span className="text-xs text-gray-600">Q{currentQuestion + 1}</span>
                </div>
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-sm">
                  <p className="text-gray-800 whitespace-pre-wrap text-sm sm:text-base">
                    {sessionData.questions[currentQuestion].question}
                  </p>
                </div>
              </div>
            </div>
          )}



          {/* Auto-scroll anchor */}
          <div ref={chatEndRef} className="h-1" />
        </div>

        {/* Input Area - Fixed at bottom */}
        {showCurrentQuestion && !submitted && (
          <div className="p-3 sm:p-4 flex-shrink-0 border-t border-white/20 bg-white/5 backdrop-blur-sm">
            <div className="relative flex items-center rounded-2xl sm:rounded-3xl">
              <textarea
                value={currentAnswer}
                onChange={(e) => handleAnswerChange(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your answer here..."
                rows={1}
                className="w-full h-14 px-4 py-3 pr-12 rounded-xl sm:rounded-2xl focus:ring-1 focus:ring-black focus:outline-none resize-none bg-white/90 backdrop-blur-sm placeholder-gray-500 border border-gray-200/50 shadow-sm text-sm"
                style={{ minHeight: '56px', maxHeight: '120px' }}
              />

              {/* Send Button - Inside input, right-facing arrow */}
              <button
                onClick={handleNextQuestion}
                disabled={!currentAnswer.trim()}
                className={`absolute bottom-1.5 right-2 p-2 rounded-full transition-all duration-200 ${!currentAnswer.trim()
                  ? 'cursor-not-allowed'
                  : 'text-black transform hover:scale-105'
                  }`}
              >
                <SendHorizonal className="w-6 h-6" />
              </button>
            </div>

            {/* Info Text */}
            <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
              <span className="hidden sm:inline">Question {currentQuestion + 1} of {sessionData.questions.length}</span>
              <span className="sm:hidden">Q{currentQuestion + 1}/{sessionData.questions.length}</span>
              <span className="hidden sm:inline">Press Enter to send, Shift+Enter for new line</span>
              <span className="sm:hidden">Enter to send</span>
              <span className="sm:hidden">{currentQuestion === sessionData.questions.length - 1 ? 'Last' : 'Next'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConductInterview;